import { StyledDialog } from '@blackhyve/common';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContentText from '@mui/material/DialogContentText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import RichTextEditor from 'components/common/v3/RichTextEditor';
import { addRecipe, selectRecipeById, updateRecipe } from 'features/recipes/store/recipesSlice';
import isEmpty from 'lodash/isEmpty';
import { useEffect, useState } from 'react';
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import '../../../assets/css/rich-text-editor.css';
import { Alert } from '@mui/material';

/**
 * Create / Update Recipe
 * @param {Boolean} open
 * @param {Number} id
 * @param {Object} handleClose
 * @returns
 */
const RecipeCreateDialog = ({ open, handleClose, id }) => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { workspaceId } = params;
  const [note, setNote] = useState('');
  const [error, setError] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [recipeName, setRecipeName] = useState('');
  const [recipeType, setRecipeType] = useState('');
  const recipe = useSelector((state) => selectRecipeById(state, id));

  const handleKeyPress = (event) => {
    if (event.key != 'Enter') {
      return;
    }
    handleSave(event.target.value);
  };

  const handleSave = () => {
    if (recipeName === '') {
      setError('Name cannot be empty');
    } else {
      setLoading(true);
      const notesRawData = note;
      id
        ? dispatch(
            updateRecipe({
              id,
              name: recipeName,
              notes: notesRawData,
              type: recipeType !== '' ? recipeType : null,
            })
          ).then(() => {
            setLoading(false);
            handleClose();
          })
        : dispatch(
            addRecipe({
              name: recipeName,
              notes: notesRawData,
              type: recipeType !== '' ? recipeType : null,
            })
          )
            .unwrap()
            .then((payload) => {
              if (payload?.data?.id) {
                navigate(`/workspace/${workspaceId}/recipes/${payload.data.id}`);
              }
              setLoading(false);
              handleClose();
            });
    }
  };

  const handleOnChangeNotes = (value) => {
    setNote(value);
  };

  useEffect(() => {
    if (recipe) {
      setRecipeName(recipe.name);
      setRecipeType(recipe?.type ? recipe.type : '');

      if (!isEmpty(recipe.notes)) {
        setNote(recipe.notes);
      }
    }
  }, [recipe]);

  return (
    <StyledDialog
      handleClose={handleClose}
      open={open}
      title={id ? 'Update Recipe' : 'Create Recipe'}
      actions={
        <>
          <Button onClick={handleClose}>Close</Button>
          <Button variant={'contained'} onClick={handleSave}>
            {id ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      {loading && (
        <Backdrop open={true} style={{ zIndex: 13 }} sx={{ color: '#fff' }}>
          <CircularProgress className="loading" />
        </Backdrop>
      )}

      <DialogContentText>
        Recipes allow commonly used task sequences to be inserted into projects
      </DialogContentText>
      {error && <Alert severity={'error'}>{error}</Alert>}
      <Grid container spacing={1}>
        <Grid item xs={9}>
          <InputLabel htmlFor={'recipe-name-input'}>Name</InputLabel>
          <TextField
            autoFocus
            fullWidth
            required
            id={'recipe-name-input'}
            name="name"
            placeholder="Recipe Name"
            size="small"
            type="text"
            value={recipeName}
            variant="outlined"
            onChange={(event) => setRecipeName(event.target.value)}
            onKeyDown={handleKeyPress}
          />
        </Grid>
        <Grid item xs={3}>
          <InputLabel htmlFor={'recipe-type-select'}>Type</InputLabel>
          <Select
            displayEmpty
            fullWidth
            id="recipe-type-select"
            inputProps={{ 'aria-label': 'Without label' }}
            size={'small'}
            value={recipeType}
            variant={'outlined'}
            onChange={(event) => setRecipeType(event.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            <MenuItem value={'master'}>Master</MenuItem>
            <MenuItem value={'phase'}>Phase</MenuItem>
            <MenuItem value={'lookahead'}>Lookahead</MenuItem>
            <MenuItem value={'wwp'}>Weekly Work Plan</MenuItem>
          </Select>
        </Grid>
        <Grid item xs={12}>
          <RichTextEditor handleOnChange={handleOnChangeNotes} readOnly={false} text={note} />
        </Grid>
      </Grid>
    </StyledDialog>
  );
};

export default RecipeCreateDialog;
